import { LookupKeys } from '@ag-common-lib/public-api';
import { AlignItems } from './template-builder-align-items.model';
import { FontEmphasisType } from 'ag-common-svc/lib/components/administration/template-builder/template-editor-page/models/template-builder-text-theme.model';

export enum ButtonStylingMode {
  contained = 'contained',
  outlined = 'outlined',
  text = 'text',
}

export const ButtonStylingModeMap = new Map([
  [ButtonStylingMode.contained, 'Contained'],
  [ButtonStylingMode.outlined, 'Outlined'],
  [ButtonStylingMode.text, 'Text'],
]);

export const BUTTON_STYLING_MODE_LOOKUP = [
  ButtonStylingMode.contained,
  ButtonStylingMode.outlined,
  ButtonStylingMode.text,
].map(value => {
  return { [LookupKeys.value]: value, [LookupKeys.description]: ButtonStylingModeMap.get(value) };
});

export enum ButtonType {
  normal = 'normal',
  default = 'default',
  success = 'success',
  danger = 'danger',
}

export const ButtonTypeMap = new Map([
  [ButtonType.normal, 'Normal'],
  [ButtonType.default, 'Default'],
  [ButtonType.success, 'Success'],
  [ButtonType.danger, 'Danger'],
]);

export const BUTTON_TYPE_LOOKUP = [ButtonType.normal, ButtonType.default, ButtonType.success, ButtonType.danger].map(
  value => {
    return { [LookupKeys.value]: value, [LookupKeys.description]: ButtonTypeMap.get(value) };
  },
);

export enum ButtonActionType {
  link = 'link',
  mailTo = 'mailTo',
  phone = 'phone',
}

export const ButtonActionMap = new Map([
  [ButtonActionType.link, 'Open Page'],
  [ButtonActionType.mailTo, 'Send Email'],
  [ButtonActionType.phone, 'Call Phone Number'],
]);

export const BUTTON_ACTION_TYPE_LOOKUP = [ButtonActionType.link, ButtonActionType.mailTo, ButtonActionType.phone].map(
  value => {
    return { [LookupKeys.value]: value, [LookupKeys.description]: ButtonActionMap.get(value) };
  },
);

export enum ButtonAlignment {
  left = 'left',
  center = 'center',
  right = 'right',
}

export const ButtonAlignmentMap = new Map([
  [ButtonAlignment.left, 'Left'],
  [ButtonAlignment.center, 'Center'],
  [ButtonAlignment.right, 'Right'],
]);

export const BUTTON_ALIGNMENT_LOOKUP = [ButtonAlignment.left, ButtonAlignment.center, ButtonAlignment.right].map(
  value => {
    return { value: value, text: ButtonAlignmentMap.get(value), hint: ButtonAlignmentMap.get(value) };
  },
);

export enum ButtonNavigateTo {
  portal = 'portal',
  web = 'web',
}

export const ButtonNavigateToMap = new Map([
  [ButtonNavigateTo.portal, 'Portal'],
  [ButtonNavigateTo.web, 'Web'],
]);

export const BUTTON_NAVIGATE_TO_LOOKUP = [ButtonNavigateTo.portal, ButtonNavigateTo.web].map(value => {
  return { [LookupKeys.value]: value, [LookupKeys.description]: ButtonNavigateToMap.get(value) };
});

export enum HrefTarget {
  blank = '_blank',
  self = '_self',
}

export const HrefTargetMap = new Map([
  [HrefTarget.blank, 'New Tab'],
  [HrefTarget.self, 'Same Tab'],
]);

export const HREF_TARGET_LOOKUP = [HrefTarget.blank, HrefTarget.self].map(value => {
  return { [LookupKeys.value]: value, [LookupKeys.description]: HrefTargetMap.get(value) };
});

export class LinkButtonActionPayload {
  href: string = '';
  target: HrefTarget = HrefTarget.blank;
  navigateTo: ButtonNavigateTo = ButtonNavigateTo.web;
  portalPage: string = '';
}

export class MailToButtonActionPayload {
  to: string = '';
  subject: string = '';
  body: string = '';
}

export class PhoneButtonActionPayload {
  number?: number = null;
}

type TemplateBuilderActionPayloadTypeMap = {
  [ButtonActionType.link]: LinkButtonActionPayload;
  [ButtonActionType.mailTo]: MailToButtonActionPayload;
  [ButtonActionType.phone]: PhoneButtonActionPayload;
};

type TemplateBuilderActionPayloadType<T extends ButtonActionType> = TemplateBuilderActionPayloadTypeMap[T];

export class ButtonWidgetData<T extends ButtonActionType = ButtonActionType> {
  text?: string = 'Button Text';
  icon?: string = null;
  stylingMode: ButtonStylingMode = ButtonStylingMode.contained;
  type: ButtonType = ButtonType.default;
  alignItems: AlignItems;
  color: string = 'var(--pb-default-accent)';
  invertedColor: string = 'var(--pb-default-inverted-text)';
  fontFamily: string;
  fontEmphasis: FontEmphasisType[];
  borderRadius: number = 4;

  constructor(
    public actionType: T,
    public actionPayload?: TemplateBuilderActionPayloadType<T>,
  ) {}
}
